// Betway Leagues admin page.  Add / toggle / remove entries in the
// whitelist used by Single bets, X5 Rollover and Top-20 Rollover.
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { AlertTriangle, Plus, Trash2, RefreshCw, Globe } from "lucide-react";
import TopNav from "../components/TopNav";

export default function BetwayLeaguesPage() {
  const { token } = useAuth();
  const auth = token ? { Authorization: `Bearer ${token}` } : {};
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [onlyFailing, setOnlyFailing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [adding, setAdding] = useState({
    betway_name: "", country: "", is_international: false, app_aliases: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, prev] = await Promise.all([
        axios.get(`${API}/betway-leagues/list`, { headers: auth }),
        axios.get(`${API}/betway-leagues/preview-today`, { headers: auth }).catch(() => ({ data: null })),
      ]);
      setRows(list.data.items || []);
      setPreview(prev.data);
    } catch (e) {
      toast.error("Load failed: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const toggleManual = async (r, nextVal) => {
    try {
      await axios.post(`${API}/betway-leagues/toggle`, {
        betway_name: r.betway_name,
        country: r.country,
        manually_disabled: nextVal,
      }, { headers: auth });
      load();
    } catch (e) { toast.error("Toggle failed"); }
  };

  const restoreAuto = async (r) => {
    try {
      await axios.post(`${API}/betway-leagues/toggle`, {
        betway_name: r.betway_name,
        country: r.country,
        auto_disabled: false,
      }, { headers: auth });
      toast.success(`${r.betway_name} restored`);
      load();
    } catch (e) { toast.error("Restore failed"); }
  };

  const removeRow = async (r) => {
    try {
      await axios.post(`${API}/betway-leagues/delete`, {
        betway_name: r.betway_name, country: r.country,
      }, { headers: auth });
      toast.success(`${r.betway_name} removed`);
      load();
    } catch (e) { toast.error("Remove failed"); }
  };

  const addRow = async () => {
    if (!adding.betway_name.trim()) return toast.error("Name required");
    if (!adding.is_international && !adding.country.trim())
      return toast.error("Country required for non-international");
    try {
      await axios.post(`${API}/betway-leagues/add`, {
        betway_name: adding.betway_name.trim(),
        country: adding.is_international ? null : adding.country.trim(),
        is_international: !!adding.is_international,
        app_aliases: adding.app_aliases.split(",").map(s => s.trim()).filter(Boolean),
      }, { headers: auth });
      toast.success("Added");
      setAdding({ betway_name: "", country: "", is_international: false, app_aliases: "" });
      load();
    } catch (e) {
      toast.error("Add failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const filtered = rows.filter(r => {
    if (onlyFailing && !r.auto_disabled && !r.manually_disabled && r.match_not_found_count < 2) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (r.betway_name || "").toLowerCase().includes(q)
        || (r.country || "").toLowerCase().includes(q)
        || (r.app_aliases || []).some(a => a.toLowerCase().includes(q));
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />
      <div className="max-w-5xl mx-auto px-5 md:px-8 pt-10 md:pt-14 pb-24 space-y-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div
              className="text-[11px] uppercase inline-flex items-center gap-2 mb-3"
              style={{
                color: "var(--v3-text-3)",
                letterSpacing: "0.14em",
                fontFamily: "var(--v3-font-mono)",
              }}
            >
              <span className="h-1 w-1 rounded-full" style={{ background: "var(--v3-indigo)" }} />
              Admin · Whitelist
            </div>
            <h1
              className="text-4xl md:text-5xl"
              data-testid="bl-title"
              style={{ fontWeight: 500, letterSpacing: "-0.045em", lineHeight: 1.05 }}
            >
              Betway Leagues
            </h1>
            <p
              className="mt-4 max-w-xl text-[14px] leading-relaxed"
              style={{ color: "var(--v3-text-2)" }}
            >
              Whitelist used by Single bets, X5 Rollover and Top-20 Elite Rollover. Matched
              by <span style={{ color: "var(--v3-text)" }}>country + Betway name or
              country + any alias</span>. International competitions match by name only.
              Repeatedly-failing leagues are auto-disabled — restore them below.
            </p>
          </div>
          <Button
            size="sm"
            onClick={load}
            variant="outline"
            data-testid="bl-refresh"
            className="h-9 rounded-md text-[13px]"
            style={{ background: "transparent", border: "1px solid var(--v3-line)", color: "var(--v3-text-2)" }}
          >
            <RefreshCw className="h-[14px] w-[14px] mr-1.5" strokeWidth={1.75} /> Refresh
          </Button>
        </div>

        {preview ? (
          <Card className="bg-slate-900 border-slate-800" data-testid="bl-preview-card">
            <CardContent className="p-4 flex items-center flex-wrap gap-5 text-sm">
              <div><span className="text-slate-400">Today ({preview.date}):</span>&nbsp;
                <span className="text-white font-semibold tabular-nums">{preview.total}</span>&nbsp;
                games on schedule.</div>
              <div className="text-emerald-300 tabular-nums">✓ {preview.kept} kept</div>
              <div className="text-rose-300 tabular-nums">✗ {preview.removed} removed</div>
            </CardContent>
          </Card>
        ) : null}

        {/* Add row */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2">
            <Plus className="h-4 w-4 text-emerald-300" /> Add a league
          </CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <Input placeholder="Betway name (e.g. Premier League)" value={adding.betway_name}
                   onChange={e => setAdding({ ...adding, betway_name: e.target.value })}
                   className="bg-slate-950 border-slate-700 text-white md:col-span-2"
                   data-testid="bl-add-name" />
            <Input placeholder="Country (empty if international)"
                   disabled={adding.is_international}
                   value={adding.country}
                   onChange={e => setAdding({ ...adding, country: e.target.value })}
                   className="bg-slate-950 border-slate-700 text-white"
                   data-testid="bl-add-country" />
            <Input placeholder="App aliases, comma-separated"
                   value={adding.app_aliases}
                   onChange={e => setAdding({ ...adding, app_aliases: e.target.value })}
                   className="bg-slate-950 border-slate-700 text-white"
                   data-testid="bl-add-aliases" />
            <div className="flex items-center gap-3 justify-between">
              <div className="flex items-center gap-2">
                <Switch checked={adding.is_international} data-testid="bl-add-intl"
                        onCheckedChange={v => setAdding({ ...adding, is_international: !!v, country: "" })} />
                <span className="text-xs text-slate-400">Intl</span>
              </div>
              <Button size="sm" onClick={addRow} data-testid="bl-add-btn">Add</Button>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <Input placeholder="Filter by name / country / alias…" value={filter}
                 onChange={e => setFilter(e.target.value)}
                 className="bg-slate-900 border-slate-700 text-white max-w-sm"
                 data-testid="bl-filter" />
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <Switch checked={onlyFailing} onCheckedChange={v => setOnlyFailing(!!v)}
                    data-testid="bl-only-failing" />
            Only failing / disabled
          </label>
          <div className="ml-auto text-xs text-slate-500 tabular-nums">
            {filtered.length} of {rows.length}
          </div>
        </div>

        {/* Table */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-slate-400 text-left">
                <tr className="border-b border-slate-800">
                  <th className="p-3">Betway name</th>
                  <th>Country</th>
                  <th>Aliases</th>
                  <th>Status</th>
                  <th>Enabled</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={`${r.country}|${r.betway_name}`}
                      className="border-b border-slate-800/50 hover:bg-slate-800/40"
                      data-testid={`bl-row-${(r.country || 'intl')}-${r.betway_name}`}>
                    <td className="p-3 text-white font-medium">
                      {r.is_international ? (
                        <span className="inline-flex items-center gap-1">
                          <Globe className="h-3.5 w-3.5 text-indigo-300" /> {r.betway_name}
                        </span>
                      ) : r.betway_name}
                    </td>
                    <td className="text-slate-400">{r.country || "—"}</td>
                    <td className="text-slate-500 text-xs max-w-xs truncate">
                      {(r.app_aliases || []).filter(a => a !== r.betway_name).join(", ") || "—"}
                    </td>
                    <td>
                      {r.auto_disabled ? (
                        <Badge className="bg-amber-500/20 text-amber-300 flex items-center gap-1 w-fit">
                          <AlertTriangle className="h-3 w-3" /> Failing
                        </Badge>
                      ) : r.manually_disabled ? (
                        <Badge className="bg-slate-600 text-slate-200">Off</Badge>
                      ) : r.match_not_found_count >= 1 ? (
                        <Badge className="bg-slate-700 text-slate-300 tabular-nums">
                          mnf {r.match_not_found_count}
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-500/20 text-emerald-300">OK</Badge>
                      )}
                    </td>
                    <td>
                      <Switch
                        checked={!r.manually_disabled && !r.auto_disabled}
                        onCheckedChange={v => {
                          if (r.auto_disabled) restoreAuto(r);
                          else toggleManual(r, !v);
                        }}
                        data-testid={`bl-toggle-${r.betway_name}`}
                      />
                    </td>
                    <td>
                      <Button size="icon" variant="ghost"
                              onClick={() => removeRow(r)}
                              data-testid={`bl-del-${r.betway_name}`}>
                        <Trash2 className="h-4 w-4 text-rose-300" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {loading && !rows.length && (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-500">Loading…</td></tr>
                )}
                {!loading && !filtered.length && (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-500">No leagues match the filter.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
